import { FormEvent, useCallback, useEffect, useState } from "react";

import {
  createCatalogPolicyDraft,
  getCatalogPolicies,
  getCatalogPolicy,
  getCatalogPolicyAudit,
  getCatalogPolicySimulation,
  getCatalogPolicySimulations,
  recordCatalogPolicyDecision,
  simulateCatalogPolicy,
  updateCatalogPolicyDraft,
  validateCatalogPolicy,
} from "./api";
import type {
  CatalogPolicyAuditEvent,
  CatalogPolicyDetail,
  CatalogPolicySimulation,
  CatalogPolicySimulationEvidenceSummary,
  CatalogPolicySummary,
  CatalogPolicyValidation,
} from "./types";
import { formatCount } from "./numberFormat";


type PricingRuleForm = {
  key: string;
  name: string;
  categoryPattern: string;
  articlePattern: string;
  foldText: string;
  operator: "lt" | "gte" | "gt";
  threshold: string;
  margin: string;
};

type PolicyForm = {
  comment: string;
  taxProfile: string;
  taxPercent: string;
  generalMargin: string;
  sourceMaxAgeMinutes: string;
  priceSnapshotMaxAgeHours: string;
  maxAggregateDeltaPercent: string;
  maxProductsOver20Percent: string;
  maxProductsOver50Eur: string;
  maxQuarantinedRows: string;
  minComparableProducts: string;
  approverUsernames: string;
  requiredApprovals: string;
  rounding: string;
  allowedCategories: string;
  excludedBrands: string;
  excludedCategories: string;
  acronyms: string;
  rules: PricingRuleForm[];
};

const POLICY_SCHEMA = "abora-idirecto-transform-rules-v1";

const EMPTY_FORM: PolicyForm = {
  comment: "",
  taxProfile: "IGIC_7",
  taxPercent: "7",
  generalMargin: "",
  sourceMaxAgeMinutes: "90",
  priceSnapshotMaxAgeHours: "24",
  maxAggregateDeltaPercent: "15",
  maxProductsOver20Percent: "50",
  maxProductsOver50Eur: "5",
  maxQuarantinedRows: "20",
  minComparableProducts: "1",
  approverUsernames: "",
  requiredApprovals: "1",
  rounding: "legacy-50-90",
  allowedCategories: "",
  excludedBrands: "",
  excludedCategories: "",
  acronyms: "",
  rules: [],
};

function objectValue(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.map(String) : [];
}

function lines(value: string): string[] {
  return [...new Set(value.split(/\r?\n/).map((item) => item.trim()).filter(Boolean))];
}

function displayPercentFromRate(value: unknown): string {
  const rate = Number(value);
  if (!Number.isFinite(rate)) return "7";
  return String(Math.round((rate * 100 + Number.EPSILON) * 1_000_000) / 1_000_000);
}

function policyToForm(policy: CatalogPolicyDetail): PolicyForm {
  const configuration = objectValue(policy.configuration);
  const pricing = objectValue(configuration.pricing);
  const filters = objectValue(configuration.filters);
  const categoryDisplay = objectValue(configuration.category_display);
  const approval = objectValue(configuration.approval);
  const commercialThresholds = objectValue(approval.commercial_thresholds);
  const rawRules = Array.isArray(pricing.overrides) ? pricing.overrides : [];
  return {
    comment: policy.comment,
    taxProfile: String(pricing.tax_profile ?? "IGIC_7"),
    taxPercent: pricing.tax_rate === undefined ? "7" : displayPercentFromRate(pricing.tax_rate),
    generalMargin: pricing.general_margin_percent === undefined
      ? ""
      : String(pricing.general_margin_percent),
    sourceMaxAgeMinutes: String(approval.source_max_age_minutes ?? "90"),
    priceSnapshotMaxAgeHours: String(approval.price_snapshot_max_age_hours ?? "24"),
    maxAggregateDeltaPercent: String(commercialThresholds.max_aggregate_absolute_delta_percent ?? "15"),
    maxProductsOver20Percent: String(commercialThresholds.max_products_over_20_percent ?? "50"),
    maxProductsOver50Eur: String(commercialThresholds.max_products_over_50_eur ?? "5"),
    maxQuarantinedRows: String(commercialThresholds.max_quarantined_rows ?? "20"),
    minComparableProducts: String(commercialThresholds.min_comparable_products ?? "1"),
    approverUsernames: stringArray(approval.approver_usernames).join("\n"),
    requiredApprovals: String(approval.required_approvals ?? "1"),
    rounding: String(pricing.rounding ?? "legacy-50-90"),
    allowedCategories: stringArray(filters.allowed_main_categories).join("\n"),
    excludedBrands: stringArray(filters.excluded_brand_contains).join("\n"),
    excludedCategories: stringArray(filters.excluded_category_contains).join("\n"),
    acronyms: stringArray(categoryDisplay.acronyms).join("\n"),
    rules: rawRules.map((rawRule, index) => {
      const rule = objectValue(rawRule);
      return {
        key: `${policy.id}-${index}-${String(rule.name ?? "rule")}`,
        name: String(rule.name ?? ""),
        categoryPattern: String(rule.category_pattern ?? ".*"),
        articlePattern: String(rule.article_pattern ?? ""),
        foldText: rule.fold_text === true ? "yes" : "no",
        operator: rule.base_operator === "gt" ? "gt" : rule.base_operator === "gte" ? "gte" : "lt",
        threshold: String(rule.base_threshold ?? ""),
        margin: String(rule.margin_percent ?? ""),
      };
    }),
  };
}

function numeric(value: string, label: string, minimum: number, maximum: number): string {
  const parsed = Number(value.replace(",", "."));
  if (!Number.isFinite(parsed) || parsed < minimum || parsed > maximum) {
    throw new Error(`${label} debe estar entre ${minimum} y ${maximum}.`);
  }
  return String(parsed);
}

function integerNumeric(value: string, label: string, minimum: number, maximum: number): number {
  const parsed = Number(numeric(value, label, minimum, maximum));
  if (!Number.isInteger(parsed)) throw new Error(`${label} debe ser un número entero.`);
  return parsed;
}

function buildConfiguration(policy: CatalogPolicyDetail, form: PolicyForm): Record<string, unknown> {
  const base = objectValue(policy.configuration);
  const ruleNames = form.rules.map((rule) => rule.name.trim());
  if (ruleNames.some((name) => !name) || new Set(ruleNames).size !== ruleNames.length) {
    throw new Error("Cada regla debe tener un nombre único.");
  }
  const taxPercent = numeric(form.taxPercent, "El IGIC", 0, 100);
  const generalMargin = numeric(form.generalMargin, "El margen general", 0, 1000);
  const sourceMaxAgeMinutes = integerNumeric(
    form.sourceMaxAgeMinutes,
    "La vigencia máxima del CSV",
    30,
    1440,
  );
  const priceSnapshotMaxAgeHours = integerNumeric(
    form.priceSnapshotMaxAgeHours,
    "La vigencia máxima del snapshot de precios",
    1,
    168,
  );
  const maxAggregateDeltaPercent = numeric(form.maxAggregateDeltaPercent, "El delta agregado máximo", 0, 1000);
  const maxProductsOver20Percent = integerNumeric(form.maxProductsOver20Percent, "El máximo de cambios superiores al 20 %", 0, 100000);
  const maxProductsOver50Eur = integerNumeric(form.maxProductsOver50Eur, "El máximo de cambios superiores a 50 €", 0, 100000);
  const maxQuarantinedRows = integerNumeric(form.maxQuarantinedRows, "El máximo de cuarentenas", 0, 100000);
  const minComparableProducts = integerNumeric(form.minComparableProducts, "El mínimo de comparables", 0, 100000);
  const approverUsernames = lines(form.approverUsernames);
  const requiredApprovals = integerNumeric(form.requiredApprovals, "El quorum", 1, Math.max(1, approverUsernames.length));
  if (approverUsernames.length === 0) throw new Error("Debe configurarse al menos un responsable de aprobación.");
  const overrides = form.rules.map((rule) => ({
    name: rule.name.trim(),
    category_pattern: rule.categoryPattern.trim() || ".*",
    ...(rule.articlePattern.trim() ? { article_pattern: rule.articlePattern.trim() } : {}),
    ...(rule.foldText === "yes" ? { fold_text: true } : {}),
    base_operator: rule.operator,
    base_threshold: numeric(rule.threshold, `El umbral de ${rule.name}`, 0, 1_000_000),
    margin_percent: numeric(rule.margin, `El margen de ${rule.name}`, 0, 1000),
  }));
  return {
    ...base,
    schema: POLICY_SCHEMA,
    rules_version: String(base.rules_version ?? `idirecto-prestashop-v2-draft-${policy.version}`),
    policy_status: policy.environment === "production" ? base.policy_status : "draft-unapproved",
    approval: {
      ...(typeof objectValue(base.approval).initial_empty_snapshot_id === "string"
        ? { initial_empty_snapshot_id: objectValue(base.approval).initial_empty_snapshot_id } : {}),
      source_max_age_minutes: sourceMaxAgeMinutes,
      price_snapshot_max_age_hours: priceSnapshotMaxAgeHours,
      commercial_thresholds: {
        max_aggregate_absolute_delta_percent: maxAggregateDeltaPercent,
        max_products_over_20_percent: maxProductsOver20Percent,
        max_products_over_50_eur: maxProductsOver50Eur,
        max_quarantined_rows: maxQuarantinedRows,
        min_comparable_products: minComparableProducts,
      },
      approver_usernames: approverUsernames,
      required_approvals: requiredApprovals,
    },
    target: policy.environment === "production" ? objectValue(base.target) : {
      environment: "TEST-only",
      shop_key: "test-default-shop",
      language: "es-ES",
      currency: "EUR",
      synthetic_fixture: false,
      real_source_canary: false,
      real_source_full_test: true,
    },
    input: {
      delimiter: ";",
      max_input_bytes: 268435456,
      max_rows: 100000,
      required_headers: ["sku", "brand", "category", "item_name", "price", "quantity"],
    },
    filters: {
      allowed_main_categories: lines(form.allowedCategories),
      excluded_brand_contains: lines(form.excludedBrands),
      excluded_category_contains: lines(form.excludedCategories),
    },
    category_display: {
      case_policy: "sentence-case-with-acronyms",
      path_separator: " > ",
      acronyms: lines(form.acronyms),
    },
    pricing: {
      tax_profile: form.taxProfile.trim() || "IGIC_7",
      tax_rate: String(Number(taxPercent) / 100),
      general_margin_percent: generalMargin,
      overrides,
      rounding: form.rounding,
    },
    data_quality: {
      barcode: "valid-ean13-or-omit",
      product_description_leading_hyphen: "allow",
      missing_brand: "quarantine",
      name_max_length: 120,
      description_short_max_length: 780,
    },
    output: {
      publication_policy: "disable",
      supplier_key: "idirecto",
      stock_action: "set_absolute",
      visibility: "both",
      available_for_order: "1",
      show_price: "1",
      condition: "new",
      allowed_html_tags: ["p", "br", "strong", "em", "ul", "ol", "li"],
    },
  };
}

function actionLabel(action: string): string {
  const labels: Record<string, string> = {
    "catalog.policy.draft_created": "Borrador creado",
    "catalog.policy.draft_updated": "Borrador guardado",
    "catalog.policy.validation_passed": "Validación correcta",
    "catalog.policy.validation_failed": "Validación con errores",
    "catalog.policy.simulation_completed": "Simulación de impacto completada",
  };
  return labels[action] ?? action;
}

function formatAuditDate(value: string): string {
  return new Intl.DateTimeFormat("es-ES", {
    dateStyle: "short",
    timeStyle: "short",
    timeZone: "Atlantic/Canary",
  }).format(new Date(value));
}

function formatInteger(value: number): string {
  return formatCount(value);
}

function formatMoney(value: string | null): string {
  if (value === null) return "—";
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
  }).format(Number(value));
}

function formatPercent(value: string | null): string {
  if (value === null) return "—";
  return `${new Intl.NumberFormat("es-ES", { maximumFractionDigits: 2 }).format(Number(value))} %`;
}

function formatAge(seconds: number): string {
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min`;
  if (seconds < 86400) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return minutes > 0 ? `${hours} h ${minutes} min` : `${hours} h`;
  }
  return `${Math.floor(seconds / 86400)} d`;
}

function readinessReason(code: string): string {
  const labels: Record<string, string> = {
    FRESHNESS_POLICY_UNCONFIGURED: "La evidencia antigua no tiene límites de frescura sellados",
    APPROVAL_POLICY_UNCONFIGURED: "La evidencia antigua no tiene política comercial ni responsables sellados",
    SIMULATION_DATA_BLOCKED: "La simulación contiene bloqueos de datos",
    SOURCE_SNAPSHOT_STALE: "El CSV de iDirecto ha caducado",
    SOURCE_TIMESTAMP_IN_FUTURE: "La fecha del CSV está en el futuro",
    PRICE_SNAPSHOT_STALE: "La captura de precios ha caducado",
    PRICE_SNAPSHOT_TIMESTAMP_IN_FUTURE: "La fecha de la captura de precios está en el futuro",
    COMMERCIAL_AGGREGATE_DELTA_EXCEEDED: "El delta agregado absoluto supera el máximo",
    COMMERCIAL_OVER_20_PERCENT_EXCEEDED: "Hay demasiados precios con variación mínima del 20 %",
    COMMERCIAL_OVER_50_EUR_EXCEEDED: "Hay demasiados precios con variación mínima de 50 €",
    COMMERCIAL_QUARANTINE_EXCEEDED: "La cuarentena supera el máximo permitido",
    COMMERCIAL_COMPARABLES_INSUFFICIENT: "No hay suficientes precios comparables",
  };
  return labels[code] ?? code;
}

function commercialCheckLabel(code: string): string {
  const labels: Record<string, string> = {
    COMMERCIAL_AGGREGATE_DELTA_EXCEEDED: "Delta agregado absoluto máximo (%)",
    COMMERCIAL_OVER_20_PERCENT_EXCEEDED: "Variaciones de al menos 20 %",
    COMMERCIAL_OVER_50_EUR_EXCEEDED: "Variaciones de al menos 50 €",
    COMMERCIAL_QUARANTINE_EXCEEDED: "Filas en cuarentena",
    COMMERCIAL_COMPARABLES_INSUFFICIENT: "Mínimo de productos comparables",
  };
  return labels[code] ?? code;
}

function approvalProgressLabel(status: string, approved: number, required: number | null): string {
  if (status === "complete") return "QUORUM COMPLETO";
  if (status === "rejected") return "RECHAZADA";
  if (status === "unconfigured") return "SIN FLUJO DE FIRMAS";
  if (status === "blocked") return "FIRMA BLOQUEADA";
  return `${approved}/${required ?? "—"} FIRMAS`;
}

function issueLabel(code: string): string {
  const labels: Record<string, string> = {
    MAIN_CATEGORY_NOT_ALLOWED: "Categoría principal fuera del alcance",
    BRAND_EXCLUDED: "Marca excluida",
    CATEGORY_EXCLUDED: "Categoría excluida",
    BRAND_MISSING: "Marca vacía en cuarentena",
    PRICE_INVALID: "Coste inválido",
    CANON_INVALID: "Canon inválido",
    DUPLICATE_REFERENCE: "Referencia duplicada",
  };
  return labels[code] ?? code;
}

function deltaBandLabel(code: string): string {
  const labels: Record<string, string> = {
    new_in_proposal: "Nuevo en la propuesta",
    unchanged: "Sin cambio",
    increase_from_zero: "Sube desde 0 €",
    decrease_from_zero: "Baja desde 0 €",
    increase_under_5_percent: "Sube menos del 5 %",
    increase_5_to_20_percent: "Sube entre 5 % y 20 %",
    increase_over_20_percent: "Sube más del 20 %",
    decrease_under_5_percent: "Baja menos del 5 %",
    decrease_5_to_20_percent: "Baja entre 5 % y 20 %",
    decrease_over_20_percent: "Baja más del 20 %",
  };
  return labels[code] ?? code;
}

export function PolicyConfiguration({
  focusPolicyId,
  focusEvidenceId,
  production = false,
}: {
  focusPolicyId?: string;
  focusEvidenceId?: string;
  production?: boolean;
}) {
  const [policies, setPolicies] = useState<CatalogPolicySummary[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [policy, setPolicy] = useState<CatalogPolicyDetail | null>(null);
  const [form, setForm] = useState<PolicyForm>(EMPTY_FORM);
  const [audit, setAudit] = useState<CatalogPolicyAuditEvent[]>([]);
  const [validation, setValidation] = useState<CatalogPolicyValidation | null>(null);
  const [simulation, setSimulation] = useState<CatalogPolicySimulation | null>(null);
  const [simulationEvidence, setSimulationEvidence] = useState<CatalogPolicySimulationEvidenceSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [approvalComment, setApprovalComment] = useState("");

  const loadPolicies = useCallback(async (preferredId?: string) => {
    const nextPolicies = await getCatalogPolicies();
    setPolicies(nextPolicies);
    const nextId = preferredId || selectedId || nextPolicies[0]?.id || "";
    setSelectedId(nextId);
    if (!nextId) {
      setPolicy(null);
      setAudit([]);
      setSimulationEvidence([]);
      setForm(EMPTY_FORM);
    }
    return nextId;
  }, [selectedId]);

  const loadPolicy = useCallback(async (policyId: string) => {
    if (!policyId) return;
    const [detail, events, evidence] = await Promise.all([
      getCatalogPolicy(policyId),
      getCatalogPolicyAudit(policyId),
      production ? Promise.resolve([]) : getCatalogPolicySimulations(policyId),
    ]);
    setPolicy(detail);
    setForm(policyToForm(detail));
    setAudit(events);
    setSimulationEvidence(evidence);
    setValidation(null);
  }, [production]);

  useEffect(() => {
    loadPolicies(focusPolicyId)
      .then(async (policyId) => {
        if (policyId) {
          await loadPolicy(policyId);
          if (!production && focusEvidenceId && policyId === focusPolicyId) {
            setSimulation(await getCatalogPolicySimulation(policyId, focusEvidenceId));
            setNotice("Evidencia exacta del ciclo abierta. No se ha modificado PrestaShop.");
          }
        }
      })
      .catch((loadError) => setError(loadError instanceof Error ? loadError.message : "No se pudo cargar la configuración."))
      .finally(() => setLoading(false));
  }, []); // La selección inicial solo se resuelve una vez.

  useEffect(() => {
    setSimulation(null);
  }, [form]);

  async function selectPolicy(policyId: string) {
    setSelectedId(policyId);
    setLoading(true);
    setError("");
    setNotice("");
    try {
      await loadPolicy(policyId);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "No se pudo abrir la versión.");
    } finally {
      setLoading(false);
    }
  }

  async function createDraft() {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const created = await createCatalogPolicyDraft();
      await loadPolicies(created.id);
      await loadPolicy(created.id);
      setNotice(`Borrador v${created.version} creado. Completa los valores antes de validarlo.`);
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : "No se pudo crear el borrador.");
    } finally {
      setBusy(false);
    }
  }

  async function persistDraft(): Promise<CatalogPolicyDetail> {
    if (!policy) throw new Error("No hay un borrador seleccionado.");
    const configuration = buildConfiguration(policy, form);
    const updated = await updateCatalogPolicyDraft(
      policy.id,
      policy.config_sha256,
      configuration,
      form.comment.trim(),
    );
    setPolicy(updated);
    setForm(policyToForm(updated));
    setValidation(null);
    setSimulation(null);
    await loadPolicies(updated.id);
    return updated;
  }

  async function saveDraft(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const updated = await persistDraft();
      setAudit(await getCatalogPolicyAudit(updated.id));
      setNotice("Borrador guardado. Aún no afecta a ninguna actualización.");
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "No se pudo guardar el borrador.");
    } finally {
      setBusy(false);
    }
  }

  async function validateDraft() {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const updated = await persistDraft();
      const result = await validateCatalogPolicy(updated.id);
      setValidation(result);
      setAudit(await getCatalogPolicyAudit(updated.id));
      setNotice(result.valid
        ? result.production_activation?.state === "ready"
          ? "Configuración validada y lista para los próximos ciclos. No se ha iniciado ninguna actualización ni modificado productos."
          : "La estructura es válida. Todavía falta simular el impacto antes de activarla."
        : "El borrador necesita correcciones antes de poder simularse.");
    } catch (validationError) {
      setError(validationError instanceof Error ? validationError.message : "No se pudo validar el borrador.");
    } finally {
      setBusy(false);
    }
  }

  async function simulateDraft() {
    setBusy(true);
    setError("");
    setNotice("");
    setSimulation(null);
    try {
      const updated = await persistDraft();
      const checked = await validateCatalogPolicy(updated.id);
      setValidation(checked);
      if (!checked.valid) {
        setAudit(await getCatalogPolicyAudit(updated.id));
        setNotice("El borrador necesita correcciones antes de poder simularse.");
        return;
      }
      const result = await simulateCatalogPolicy(updated.id);
      setSimulation(result);
      const [events, evidence] = await Promise.all([
        getCatalogPolicyAudit(updated.id),
        getCatalogPolicySimulations(updated.id),
      ]);
      setAudit(events);
      setSimulationEvidence(evidence);
      setNotice(result.status === "pass"
        ? "Simulación completada y evidencia sellada. No se ha modificado PrestaShop."
        : "Simulación completada con bloqueos de datos; revise el resultado antes de continuar.");
    } catch (simulationError) {
      setError(simulationError instanceof Error ? simulationError.message : "No se pudo simular la política.");
    } finally {
      setBusy(false);
    }
  }

  async function openSimulationEvidence(evidenceId: string) {
    if (!policy) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      setSimulation(await getCatalogPolicySimulation(policy.id, evidenceId));
      setApprovalComment("");
      setNotice("Evidencia inmutable reabierta. No se ha recalculado ni modificado PrestaShop.");
    } catch (evidenceError) {
      setError(evidenceError instanceof Error ? evidenceError.message : "No se pudo abrir la evidencia.");
    } finally {
      setBusy(false);
    }
  }

  async function submitDecision(decision: "approve" | "reject") {
    if (!policy || !simulation) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const updated = await recordCatalogPolicyDecision(
        policy.id,
        simulation.evidence.id,
        decision,
        approvalComment,
      );
      setSimulation(updated);
      setApprovalComment("");
      setSimulationEvidence(await getCatalogPolicySimulations(policy.id));
      setNotice(decision === "approve"
        ? "Aprobación inmutable registrada. No se ha activado ni modificado PrestaShop."
        : "Rechazo inmutable registrado. La evidencia no podrá completar el quorum.");
    } catch (decisionError) {
      setError(decisionError instanceof Error ? decisionError.message : "No se pudo registrar la decisión.");
    } finally {
      setBusy(false);
    }
  }

  function updateRule(key: string, field: keyof Omit<PricingRuleForm, "key">, value: string) {
    setForm((current) => ({
      ...current,
      rules: current.rules.map((rule) => rule.key === key ? { ...rule, [field]: value } : rule),
    }));
  }

  function moveRule(index: number, direction: -1 | 1) {
    setForm((current) => {
      const destination = index + direction;
      if (destination < 0 || destination >= current.rules.length) return current;
      const rules = [...current.rules];
      [rules[index], rules[destination]] = [rules[destination], rules[index]];
      return { ...current, rules };
    });
  }

  const editable = policy?.status === "draft";

  return <section id="configuracion" className="page-section configuration-panel route-panel" aria-labelledby="configuration-title">
    <div className="panel-heading configuration-heading">
      <div><p className="eyebrow">Política versionada · {production ? "PROD" : "TEST"}</p><h2 id="configuration-title">Política del catálogo</h2></div>
      {!production ? <button className="primary-button compact" type="button" disabled={busy} onClick={() => void createDraft()}>
        {busy ? "Procesando…" : "Nuevo borrador"}
      </button> : null}
    </div>
    <div className="configuration-safety"><strong>Sin cambios inmediatos en PrestaShop</strong><span>{production ? "Guardar conserva el borrador. Guardar y validar deja las reglas listas para los próximos ciclos, sin iniciar una actualización ni modificar planes ya sellados." : "Guardar y validar no activa la política ni modifica productos."}</span></div>
    {error ? <p className="form-error" role="alert">{error}</p> : null}
    {notice ? <p className="notice" role="status">{notice}</p> : null}
    {loading ? <p className="empty-state">Cargando políticas…</p> : null}
    {!loading && policies.length === 0 ? <div className="configuration-empty"><strong>No hay borradores todavía</strong><p>{production ? "No hay una configuración productiva disponible. Revisa el perfil del servidor." : "Crea uno vacío y define los valores comerciales. No se copiarán automáticamente las reglas TEST inconsistentes."}</p></div> : null}
    {policies.length > 0 && policy ? <>
      <div className="policy-toolbar">
        <label htmlFor="policy-version">Versión
          <select id="policy-version" value={selectedId} onChange={(event) => void selectPolicy(event.target.value)}>
            {policies.map((item) => <option key={item.id} value={item.id}>v{item.version} · {item.status_label} · {item.environment.toUpperCase()}</option>)}
          </select>
        </label>
        <dl>
          <div><dt>Estado</dt><dd>{policy.status_label}</dd></div>
          <div><dt>Autor</dt><dd>{policy.created_by}</dd></div>
          <div><dt>Hash</dt><dd><code>{policy.config_sha256.slice(0, 12)}…</code></dd></div>
        </dl>
      </div>
      <form className="policy-form" onSubmit={saveDraft}>
        <fieldset disabled={!editable || busy}>
          <legend>Precios y fiscalidad</legend>
          <p className="field-help">Los porcentajes se introducen como números normales: 20 significa 20 %.</p>
          <div className="form-grid three-columns">
            <label htmlFor="tax-profile">Perfil fiscal
              <input id="tax-profile" value={form.taxProfile} onChange={(event) => setForm({ ...form, taxProfile: event.target.value })} required />
            </label>
            <label htmlFor="tax-percent">IGIC (%)
              <input id="tax-percent" inputMode="decimal" value={form.taxPercent} onChange={(event) => setForm({ ...form, taxPercent: event.target.value })} required />
            </label>
            <label htmlFor="general-margin">Margen general (%)
              <input id="general-margin" inputMode="decimal" placeholder="Pendiente de definir" value={form.generalMargin} onChange={(event) => setForm({ ...form, generalMargin: event.target.value })} required />
            </label>
          </div>
          <label htmlFor="rounding-policy">Redondeo
            <select id="rounding-policy" value={form.rounding} onChange={(event) => setForm({ ...form, rounding: event.target.value })}>
              <option value="legacy-50-90">Legado: terminaciones 0,50 / 0,90</option>
            </select>
          </label>
        </fieldset>

        <fieldset disabled={!editable || busy}>
          <legend>{production ? "Calidad para aprobación automática" : "Política de aprobación"}</legend>
          <p className="field-help">{production ? "El presupuesto de cuarentena queda fijado en cada ciclo validado. Las filas en cuarentena no se importan; las protecciones y las reconciliaciones siguen siendo obligatorias." : "Los límites y responsables quedan sellados en cada evidencia. La antigüedad se recalcula al consultarla y una firma nunca activa la política."}</p>
          {!production ? <div className="form-grid freshness-fields">
            <label htmlFor="source-max-age">CSV iDirecto: vigencia máxima (minutos)
              <input id="source-max-age" type="number" min="30" max="1440" step="1" value={form.sourceMaxAgeMinutes} onChange={(event) => setForm({ ...form, sourceMaxAgeMinutes: event.target.value })} required />
            </label>
            <label htmlFor="price-max-age">Snapshot de precios: vigencia máxima (horas)
              <input id="price-max-age" type="number" min="1" max="168" step="1" value={form.priceSnapshotMaxAgeHours} onChange={(event) => setForm({ ...form, priceSnapshotMaxAgeHours: event.target.value })} required />
            </label>
          </div> : null}
          {!production ? <><h4 className="form-subtitle">Umbrales comerciales</h4>
          <p className="form-help">Las variaciones de precio (agregada, ≥ 20 % y ≥ 50 €) generan avisos, no bloqueos: el CSV es la fuente y se mantienen márgenes e impuestos. La frescura, la calidad y los comparables siguen siendo obligatorios.</p></> : null}
          {!production && typeof objectValue(policy.configuration.approval).initial_empty_snapshot_id === "string" ? <p className="form-help">Primera carga TEST: excepción de comparables vinculada exclusivamente al snapshot vacío {String(objectValue(policy.configuration.approval).initial_empty_snapshot_id)}. No reduce el mínimo general ni omite frescura u otros límites.</p> : null}
          <div className="form-grid approval-threshold-fields">
            {!production ? <><label htmlFor="max-aggregate-delta">Aviso de delta agregado absoluto (%)
              <input id="max-aggregate-delta" inputMode="decimal" value={form.maxAggregateDeltaPercent} onChange={(event) => setForm({ ...form, maxAggregateDeltaPercent: event.target.value })} required />
            </label>
            <label htmlFor="max-over-20">Aviso por número de variaciones ≥ 20 %
              <input id="max-over-20" type="number" min="0" step="1" value={form.maxProductsOver20Percent} onChange={(event) => setForm({ ...form, maxProductsOver20Percent: event.target.value })} required />
            </label>
            <label htmlFor="max-over-50-eur">Aviso por número de variaciones ≥ 50 €
              <input id="max-over-50-eur" type="number" min="0" step="1" value={form.maxProductsOver50Eur} onChange={(event) => setForm({ ...form, maxProductsOver50Eur: event.target.value })} required />
            </label></> : null}
            <label htmlFor="max-quarantine">Máximo de filas en cuarentena
              <input id="max-quarantine" type="number" min="0" step="1" value={form.maxQuarantinedRows} onChange={(event) => setForm({ ...form, maxQuarantinedRows: event.target.value })} required />
            </label>
            {!production ? <label htmlFor="min-comparables">Mínimo de productos comparables
              <input id="min-comparables" type="number" min="0" step="1" value={form.minComparableProducts} onChange={(event) => setForm({ ...form, minComparableProducts: event.target.value })} required />
            </label> : null}
          </div>
          {!production ? <><h4 className="form-subtitle">Responsables y quorum</h4>
          <div className="form-grid approval-owner-fields">
            <label htmlFor="approver-usernames">Usuarios responsables, uno por línea
              <textarea id="approver-usernames" rows={3} value={form.approverUsernames} onChange={(event) => setForm({ ...form, approverUsernames: event.target.value })} required />
            </label>
            <label htmlFor="required-approvals">Firmas distintas requeridas
              <input id="required-approvals" type="number" min="1" max={Math.max(1, lines(form.approverUsernames).length)} step="1" value={form.requiredApprovals} onChange={(event) => setForm({ ...form, requiredApprovals: event.target.value })} required />
            </label>
          </div>
          <div className="protected-scope"><strong>Valores iniciales</strong><span>90 minutos permite dos ciclos fallidos del timer de 30 minutos. 24 horas exige una captura diaria de precios.</span></div></> : null}
        </fieldset>

        <fieldset disabled={!editable || busy}>
          <legend>Reglas especiales de margen</legend>
          <div className="fieldset-heading"><p className="field-help">El orden importa: una regla posterior sobrescribe una anterior si ambas coinciden. Categoría o artículo: basta una coincidencia; la condición de precio siempre debe cumplirse. Usa (?!) en categoría para buscar sólo por artículo.</p><button type="button" className="secondary-button" onClick={() => setForm((current) => ({ ...current, rules: [...current.rules, { key: crypto.randomUUID(), name: "", categoryPattern: ".*", articlePattern: "", foldText: "no", operator: "lt", threshold: "", margin: "" }] }))}>Añadir regla</button></div>
          {form.rules.length === 0 ? <p className="inline-empty">Sin reglas especiales. Se aplicará únicamente el margen general.</p> : null}
          <div className="pricing-rules">
            {form.rules.map((rule, index) => <article className="pricing-rule" key={rule.key}>
              <div className="rule-order"><strong>{index + 1}</strong><button type="button" aria-label={`Subir regla ${rule.name || index + 1}`} disabled={index === 0} onClick={() => moveRule(index, -1)}>↑</button><button type="button" aria-label={`Bajar regla ${rule.name || index + 1}`} disabled={index === form.rules.length - 1} onClick={() => moveRule(index, 1)}>↓</button></div>
              <label>Nombre<input value={rule.name} onChange={(event) => updateRule(rule.key, "name", event.target.value)} required /></label>
              <label>Patrón de categoría<input value={rule.categoryPattern} onChange={(event) => updateRule(rule.key, "categoryPattern", event.target.value)} placeholder=".* para todas" required /></label>
              <label>Patrón de artículo (opcional)<input value={rule.articlePattern} onChange={(event) => updateRule(rule.key, "articlePattern", event.target.value)} /></label>
              <label>Normalización del texto<select value={rule.foldText} onChange={(event) => updateRule(rule.key, "foldText", event.target.value)}><option value="no">Sin normalizar</option><option value="yes">Minúsculas y sin tildes</option></select></label>
              <label>Condición<select value={rule.operator} onChange={(event) => updateRule(rule.key, "operator", event.target.value)}><option value="lt">Base menor que</option><option value="gte">Base mayor o igual que</option><option value="gt">Base mayor que (estricto)</option></select></label>
              <label>Umbral (€)<input inputMode="decimal" value={rule.threshold} onChange={(event) => updateRule(rule.key, "threshold", event.target.value)} required /></label>
              <label>Margen (%)<input inputMode="decimal" value={rule.margin} onChange={(event) => updateRule(rule.key, "margin", event.target.value)} required /></label>
              <button type="button" className="text-button remove-rule" onClick={() => setForm((current) => ({ ...current, rules: current.rules.filter((item) => item.key !== rule.key) }))}>Eliminar</button>
            </article>)}
          </div>
        </fieldset>

        <fieldset disabled={!editable || busy}>
          <legend>Alcance del catálogo</legend>
          <p className="field-help">Una entrada por línea. Vacío significa que todavía no se ha aprobado el alcance.</p>
          <div className="form-grid three-columns">
            <label>Categorías principales permitidas<textarea rows={7} value={form.allowedCategories} onChange={(event) => setForm({ ...form, allowedCategories: event.target.value })} /></label>
            <label>Marcas excluidas<textarea rows={7} value={form.excludedBrands} onChange={(event) => setForm({ ...form, excludedBrands: event.target.value })} /></label>
            <label>Categorías excluidas<textarea rows={7} value={form.excludedCategories} onChange={(event) => setForm({ ...form, excludedCategories: event.target.value })} /></label>
          </div>
          <label>Acrónimos que conservan mayúsculas<textarea rows={3} placeholder="USB&#10;CPU&#10;SSD" value={form.acronyms} onChange={(event) => setForm({ ...form, acronyms: event.target.value })} /></label>
          <div className="protected-scope"><strong>Protecciones fijas</strong><span>Destacados queda fuera de iDirecto. Novedades continúa bajo la lógica interna de PrestaShop. {production ? "Las altas sólo se exponen tras las verificaciones del ciclo." : "Las altas se mantienen inactivas."}</span></div>
        </fieldset>

        <fieldset disabled={!editable || busy}>
          <legend>Nota del borrador</legend>
          <label htmlFor="policy-comment">Motivo o contexto<textarea id="policy-comment" rows={3} maxLength={500} value={form.comment} onChange={(event) => setForm({ ...form, comment: event.target.value })} /></label>
        </fieldset>

        <div className="policy-actions">
          <button className="primary-button" type="submit" disabled={!editable || busy}>Guardar borrador</button>
          <button className="secondary-button" type="button" disabled={!editable || busy} onClick={() => void validateDraft()}>Guardar y validar</button>
          {!production ? <button className="secondary-button simulation-button" type="button" disabled={!editable || busy} onClick={() => void simulateDraft()}>Validar y simular impacto</button> : null}
          {!editable ? <span>Esta versión es de solo lectura.</span> : null}
        </div>
      </form>

      {validation ? <div className={`validation-result ${validation.valid ? "validation-ok" : "validation-failed"}`} role="status">
        <strong>{validation.valid ? validation.production_activation?.state === "ready" ? "Configuración lista" : "Estructura válida" : "Estructura incompleta"}</strong>
        {validation.valid ? <p>{validation.production_activation?.state === "ready" ? "Esta versión se utilizará en los próximos ciclos. Los productos y los ciclos ya iniciados no han cambiado." : "La política puede simularse con el último CSV; esta validación no la activa."}</p> : <ul>{Object.entries(validation.errors).map(([field, messages]) => <li key={field}><b>{field}</b>: {messages.join(" ")}</li>)}</ul>}
      </div> : null}

      {!production && simulationEvidence.length > 0 ? <section className="simulation-evidence-list" aria-labelledby="simulation-evidence-title">
        <div className="evidence-list-heading"><div><p className="eyebrow">Evidencia inmutable</p><h3 id="simulation-evidence-title">Simulaciones guardadas</h3></div><span>{simulationEvidence.length} disponibles</span></div>
        <div className="evidence-list-grid">{simulationEvidence.map((item) => <article key={item.id}>
          <div><strong>{formatAuditDate(item.created_at)}</strong><small>{item.created_by} · {item.status === "pass" ? "Datos sin bloqueos" : "Datos con bloqueos"}</small></div>
          <span className={`readiness-chip readiness-${item.approval_readiness.status}`}>{item.approval_readiness.eligible ? "APROBABLE AHORA" : item.approval_readiness.status === "unconfigured" ? "FRESCURA NO CONFIGURADA" : "NO APROBABLE"}</span>
          <span className={`approval-chip approval-${item.approval_progress.status}`}>{approvalProgressLabel(item.approval_progress.status, item.approval_progress.approved, item.approval_progress.required)}</span>
          <dl><div><dt>Comparables</dt><dd>{formatInteger(item.comparison.matched)}</dd></div><div><dt>Delta</dt><dd>{formatPercent(item.comparison.aggregate_delta_percent)}</dd></div><div><dt>Hash</dt><dd><code>{item.sha256.slice(0, 12)}…</code></dd></div></dl>
          <button className="row-action" type="button" disabled={busy} onClick={() => void openSimulationEvidence(item.id)}>Abrir evidencia</button>
        </article>)}</div>
      </section> : null}

      {!production && simulation ? <section className={`policy-simulation ${simulation.status === "pass" ? "simulation-pass" : "simulation-blocked"}`} aria-labelledby="simulation-title">
        <div className="simulation-heading">
          <div><p className="eyebrow">Simulación sobre el último CSV</p><h3 id="simulation-title">Impacto de precios y alcance</h3></div>
          <span className="simulation-status">{simulation.status === "pass" ? "SIN BLOQUEOS" : "CON BLOQUEOS"}</span>
        </div>
        <p className="simulation-source">Catálogo <strong>{simulation.source.name}</strong> · {formatInteger(simulation.source.rows)} filas · hash <code>{simulation.source.sha256.slice(0, 12)}…</code></p>
        <p className="simulation-evidence-proof"><strong>Evidencia sellada</strong><span>ID {simulation.evidence.id.slice(0, 8)} · hash <code>{simulation.evidence.sha256.slice(0, 12)}…</code> · {formatAuditDate(simulation.evidence.created_at)}</span></p>
        <section className={`approval-readiness readiness-${simulation.evidence.approval_readiness.status}`} aria-labelledby="approval-readiness-title">
          <div className="approval-readiness-heading"><div><p className="eyebrow">Gate dinámico</p><h4 id="approval-readiness-title">Frescura para aprobación</h4></div><span className={`readiness-chip readiness-${simulation.evidence.approval_readiness.status}`}>{simulation.evidence.approval_readiness.eligible ? "APROBABLE AHORA" : simulation.evidence.approval_readiness.status === "unconfigured" ? "NO CONFIGURADA" : "BLOQUEADA"}</span></div>
          {simulation.evidence.approval_readiness.source && simulation.evidence.approval_readiness.price_snapshot ? <div className="freshness-metrics">
            <article><span>CSV iDirecto</span><strong>{formatAge(simulation.evidence.approval_readiness.source.age_seconds)}</strong><small>Límite {formatAge(simulation.evidence.approval_readiness.source.max_age_seconds)} · {simulation.evidence.approval_readiness.source.status === "current" ? "vigente" : "caducado"}</small></article>
            <article><span>Snapshot de precios</span><strong>{formatAge(simulation.evidence.approval_readiness.price_snapshot.age_seconds)}</strong><small>Límite {formatAge(simulation.evidence.approval_readiness.price_snapshot.max_age_seconds)} · {simulation.evidence.approval_readiness.price_snapshot.status === "current" ? "vigente" : "caducado"}</small></article>
          </div> : null}
          {simulation.evidence.approval_readiness.commercial_gate ? <div className="commercial-gate"><h5>Umbrales comerciales sellados</h5><ul>{simulation.evidence.approval_readiness.commercial_gate.checks.map((check) => <li className={check.passed ? "check-pass" : check.blocking === false ? "check-warning" : "check-fail"} key={check.code}><span>{commercialCheckLabel(check.code)}{check.exception === "INITIAL_EMPTY_TEST_SNAPSHOT" ? " · Primera carga TEST: snapshot vacío verificado" : check.passed ? " · Cumple" : check.blocking === false ? " · Aviso, no bloquea" : " · No cumple"}</span><strong>{check.actual} / {check.limit}</strong></li>)}</ul></div> : null}
          {Boolean(simulation.evidence.approval_readiness.commercial_gate?.warning_reasons?.length) ? <p role="status" className="snapshot-alert warning">Variaciones de precio detectadas. No impiden aprobar ni actualizar: se usan los datos del CSV con la política de precios configurada.</p> : null}
          {simulation.evidence.approval_readiness.blocking_reasons.length > 0 ? <ul className="readiness-reasons">{simulation.evidence.approval_readiness.blocking_reasons.map((reason) => <li key={reason}>{readinessReason(reason)}</li>)}</ul> : <p className="readiness-ok">Las fuentes y los datos cumplen este gate. No implica aprobación comercial ni activación.</p>}
          <small>Evaluado {formatAuditDate(simulation.evidence.approval_readiness.evaluated_at)}. Se recalcula al abrir la evidencia.</small>
        </section>
        <section className={`approval-flow approval-${simulation.evidence.approval_progress.status}`} aria-labelledby="approval-flow-title">
          <div className="approval-flow-heading"><div><p className="eyebrow">Firmas inmutables · sin activación</p><h4 id="approval-flow-title">Aprobación documental</h4></div><span className={`approval-chip approval-${simulation.evidence.approval_progress.status}`}>{simulation.evidence.approval_progress.automatic ? "AUTOMÁTICA" : approvalProgressLabel(simulation.evidence.approval_progress.status, simulation.evidence.approval_progress.approved, simulation.evidence.approval_progress.required)}</span></div>
          <p className="approval-owners">Responsables sellados: <strong>{simulation.evidence.approval_progress.allowed_approvers.join(", ") || "No configurados"}</strong></p>
          {simulation.evidence.approval_progress.automatic_approval ? <p className="automatic-approval-proof">Aprobación automática registrada por {simulation.evidence.approval_progress.automatic_approval.activated_by} el {formatAuditDate(simulation.evidence.approval_progress.automatic_approval.created_at)}.</p> : null}
          {simulation.evidence.approval_progress.decisions.length > 0 ? <ol className="approval-decisions">{simulation.evidence.approval_progress.decisions.map((decision) => <li key={decision.id}><div><strong>{decision.approver}</strong><small>{formatAuditDate(decision.created_at)}</small></div><span>{decision.decision === "approve" ? "APROBÓ" : "RECHAZÓ"}</span>{decision.comment ? <p>{decision.comment}</p> : null}</li>)}</ol> : <p className="inline-empty">Todavía no hay decisiones registradas.</p>}
          {(simulation.evidence.approval_progress.current_user_can_approve || simulation.evidence.approval_progress.current_user_can_reject) ? <div className="approval-decision-form">
            <label htmlFor="approval-comment">Comentario de la decisión
              <textarea id="approval-comment" rows={2} maxLength={500} value={approvalComment} onChange={(event) => setApprovalComment(event.target.value)} />
            </label>
            <div><button type="button" className="secondary-button" disabled={busy || !simulation.evidence.approval_progress.current_user_can_approve} onClick={() => void submitDecision("approve")}>Registrar aprobación</button><button type="button" className="text-button reject-decision" disabled={busy || !simulation.evidence.approval_progress.current_user_can_reject} onClick={() => void submitDecision("reject")}>Registrar rechazo</button></div>
          </div> : null}
          <small>Completar el quorum no publica, activa ni aplica esta política.</small>
        </section>
        <div className="simulation-metrics">
          <article><span>Simuladas</span><strong>{formatInteger(simulation.rows.simulated)}</strong><small>de {formatInteger(simulation.rows.input)} filas</small></article>
          <article><span>Filtradas</span><strong>{formatInteger(simulation.rows.filtered)}</strong><small>fuera del alcance</small></article>
          <article><span>Cuarentena</span><strong>{formatInteger(simulation.rows.quarantined)}</strong><small>requieren revisión</small></article>
          <article><span>Rechazadas</span><strong>{formatInteger(simulation.rows.rejected)}</strong><small>errores bloqueantes</small></article>
        </div>
        <div className="simulation-price-summary">
          <div><span>Coste + canon, sin IGIC</span><strong>{formatMoney(simulation.pricing.total_base_tax_excl)}</strong></div>
          <div><span>PVP propuesto, con IGIC</span><strong>{formatMoney(simulation.pricing.total_proposed_tax_incl)}</strong></div>
          <div><span>Diferencia agregada sobre coste</span><strong>{formatPercent(simulation.pricing.uplift_from_cost_percent)}</strong></div>
        </div>
        <section className="price-comparison" aria-labelledby="price-comparison-title">
          <div className="price-comparison-heading">
            <div><p className="eyebrow">Contra PrestaShop · sólo lectura</p><h4 id="price-comparison-title">Variación frente al PVP base actual</h4></div>
            <small>Captura {formatAuditDate(simulation.current_catalog.captured_at)} · <code>{simulation.current_catalog.sha256.slice(0, 12)}…</code></small>
          </div>
          <div className="comparison-metrics">
            <article><span>Comparables</span><strong>{formatInteger(simulation.comparison.matched)}</strong><small>referencias únicas</small></article>
            <article className="metric-up"><span>Suben</span><strong>{formatInteger(simulation.comparison.increased)}</strong><small>respecto al PVP actual</small></article>
            <article className="metric-down"><span>Bajan</span><strong>{formatInteger(simulation.comparison.decreased)}</strong><small>respecto al PVP actual</small></article>
            <article><span>Sin cambio</span><strong>{formatInteger(simulation.comparison.unchanged)}</strong><small>al céntimo</small></article>
            <article><span>Nuevos</span><strong>{formatInteger(simulation.comparison.new_in_proposal)}</strong><small>sin PVP comparable</small></article>
            <article><span>Sin candidato</span><strong>{formatInteger(simulation.comparison.current_without_candidate)}</strong><small>no implica una baja</small></article>
          </div>
          <div className="comparison-totals">
            <div><span>PVP actual comparable</span><strong>{formatMoney(simulation.comparison.total_current_matched_tax_incl)}</strong></div>
            <div><span>PVP propuesto comparable</span><strong>{formatMoney(simulation.comparison.total_proposed_matched_tax_incl)}</strong></div>
            <div><span>Variación agregada</span><strong>{formatMoney(simulation.comparison.aggregate_delta_tax_incl)} · {formatPercent(simulation.comparison.aggregate_delta_percent)}</strong></div>
          </div>
          <div className="comparison-details-grid">
            <article><h5>Bandas de variación</h5><ul>{Object.entries(simulation.comparison.distribution).map(([code, count]) => <li key={code}><span>{deltaBandLabel(code)}</span><strong>{formatInteger(count)}</strong></li>)}</ul></article>
            <article><h5>Umbrales de revisión</h5><ul>
              <li><span>Cambio absoluto ≥ 5 %</span><strong>{formatInteger(simulation.comparison.thresholds.absolute_percent_gte_5)}</strong></li>
              <li><span>Cambio absoluto ≥ 20 %</span><strong>{formatInteger(simulation.comparison.thresholds.absolute_percent_gte_20)}</strong></li>
              <li><span>Cambio absoluto ≥ 10 €</span><strong>{formatInteger(simulation.comparison.thresholds.absolute_eur_gte_10)}</strong></li>
              <li><span>Cambio absoluto ≥ 50 €</span><strong>{formatInteger(simulation.comparison.thresholds.absolute_eur_gte_50)}</strong></li>
            </ul><small>Son indicadores informativos; todavía no aprueban ni bloquean.</small></article>
          </div>
          <div className="delta-examples"><h5>Mayores variaciones absolutas</h5>{simulation.comparison.examples.length === 0 ? <p>Sin precios comparables.</p> : <ol>{simulation.comparison.examples.slice(0, 5).map((example, index) => <li key={`${example.category}-${example.rule}-${index}`}><div><strong>{example.category}</strong><small>{example.rule} · {formatPercent(example.delta_percent)}</small></div><span>{formatMoney(example.current_tax_incl)} → <b>{formatMoney(example.proposed_tax_incl)}</b> <em>{formatMoney(example.delta_tax_incl)}</em></span></li>)}</ol>}</div>
          {(simulation.current_catalog.ambiguous_reference_groups > 0 || simulation.current_catalog.blank_references > 0) ? <p className="comparison-warning">Se excluyeron {formatInteger(simulation.current_catalog.ambiguous_reference_groups)} grupos ambiguos y {formatInteger(simulation.current_catalog.blank_references)} referencias vacías del cruce.</p> : null}
        </section>
        <div className="simulation-table-wrap">
          <table className="simulation-table">
            <caption>Aplicación de reglas de margen</caption>
            <thead><tr><th>Regla</th><th>Margen</th><th>Productos</th><th>Base media</th><th>PVP medio</th><th>PVP máximo</th></tr></thead>
            <tbody>{simulation.rules.map((rule) => <tr key={rule.name}>
              <td><strong>{rule.name}</strong></td><td>{formatPercent(rule.margin_percent)}</td><td>{formatInteger(rule.count)}</td><td>{formatMoney(rule.base_tax_excl.average)}</td><td>{formatMoney(rule.proposed_tax_incl.average)}</td><td>{formatMoney(rule.proposed_tax_incl.maximum)}</td>
            </tr>)}</tbody>
          </table>
        </div>
        <div className="simulation-details-grid">
          <article><h4>Filtros y bloqueos</h4>
            {Object.keys({ ...simulation.filters, ...simulation.quarantines, ...simulation.blocking_errors }).length === 0 ? <p>Sin incidencias de alcance o datos.</p> : <ul>
              {Object.entries(simulation.filters).map(([code, count]) => <li key={`filter-${code}`}><span>{issueLabel(code)}</span><strong>{formatInteger(count)}</strong></li>)}
              {Object.entries(simulation.quarantines).map(([code, count]) => <li key={`quarantine-${code}`}><span>{issueLabel(code)}</span><strong>{formatInteger(count)}</strong></li>)}
              {Object.entries(simulation.blocking_errors).map(([code, count]) => <li key={`error-${code}`}><span>{issueLabel(code)}</span><strong>{formatInteger(count)}</strong></li>)}
            </ul>}
          </article>
          <article><h4>Ejemplos de PVP más alto</h4>
            {simulation.examples.length === 0 ? <p>Sin filas simulables.</p> : <ol>{simulation.examples.slice(0, 5).map((example, index) => <li key={`${example.category}-${example.rule}-${index}`}><div><strong>{example.category}</strong><small>{example.rule} · margen {formatPercent(example.margin_percent)}</small></div><span>{formatMoney(example.base_tax_excl)} → <b>{formatMoney(example.proposed_tax_incl)}</b></span></li>)}</ol>}
          </article>
        </div>
        <div className="simulation-limitations"><strong>Qué significa este cálculo</strong><ul>{simulation.limitations.map((item) => <li key={item}>{item}</li>)}</ul></div>
      </section> : null}

      <section className="policy-audit" aria-label="Historial de la política">
        <h3>Actividad de esta versión</h3>
        {audit.length === 0 ? <p>Sin eventos todavía.</p> : <ol>{audit.map((event) => <li key={event.id}><span className={`audit-dot ${event.action.endsWith("failed") ? "audit-failed" : ""}`} /><div><strong>{actionLabel(event.action)}</strong><small>{event.actor || "Sistema"} · {formatAuditDate(event.created_at)}</small></div></li>)}</ol>}
      </section>
    </> : null}
  </section>;
}
